import type { CodeItem } from "@/types/code-values"

// Utility service for working with code values
export const codeValueService = {
  /**
   * Get the text of a code value by ID
   */
  getCodeValueText(id: number, groupName: string): string {
    const codeValue = this.getCodeValue(id, groupName)
    return codeValue?.text || ""
  },

  /**
   * Get the code of a code value by ID
   */
  getCodeValueCode(id: number, groupName: string): string {
    const codeValue = this.getCodeValue(id, groupName)
    return codeValue?.code || ""
  },

  /**
   * Get the ID of a code value by code
   */
  getCodeValueIdByCode(code: string, groupName: string): number | null {
    const codeValue = this.getCodeValueByCode(code, groupName)
    return codeValue?.id || null
  },

  /**
   * Get the text of a code value by code
   */
  getCodeValueTextByCode(code: string, groupName: string): string {
    const codeValue = this.getCodeValueByCode(code, groupName)
    return codeValue?.text || ""
  },

  /**
   * Get a code value by ID
   */
  getCodeValue(id: number, groupName: string): CodeItem | null {
    return this.getCodeValueGroup(groupName).find((c) => c.id === +id) ?? null
  },

  /**
   * Get a code value by code
   */
  getCodeValueByCode(code: string, groupName: string): CodeItem | null {
    return this.getCodeValueGroup(groupName).find((c) => c.code === code) ?? null
  },

  /**
   * Get the parent ID of a code value
   */
  getParentId(groupName: string, id: number): number {
    if (!groupName) {
      console.error("CodeValueService.getParentId groupName is empty")
      return 0
    }

    let parentId = 0
    this.getCodeValueGroup(groupName).forEach((item) => {
      if (item.id === id) {
        parentId = item.parentId || 0
      }
    })

    return parentId
  },

  /**
   * Get related code values by parent ID and parent group
   */
  getRelatedCodeValues(groupName: string, parentId: any, parentGroup: string): Array<CodeItem> {
    if (!groupName) {
      console.error("CodeValueService.getRelatedCodeValues groupName is empty")
      return []
    }

    return this.getCodeValueGroup(groupName)
      .filter((item) => item.parentId === parentId && item.parentGroup === parentGroup)
      .sort((a: CodeItem, b: CodeItem) => a.sortOrder - b.sortOrder)
  },

  /**
   * Get related code values by parent code and parent group
   */
  getRelatedCodeValuesByCode(groupName: string, parentCode: string, parentGroup: string): Array<CodeItem> {
    return this.getCodeValueGroup(groupName)
      .filter((item) => item.parentCode === parentCode && item.parentGroup === parentGroup)
      .sort((a: CodeItem, b: CodeItem) => a.sortOrder - b.sortOrder)
  },

  /**
   * Get all code values for a group
   */
  getCodeValues(groupName: string, required: boolean): Array<CodeItem> {
    if (!groupName) {
      console.error("CodeValueService.getCodeValues groupName is empty")
      return []
    }

    const result = [...this.getCodeValueGroup(groupName)]

    if (required === false) {
      // Add first empty item for optional selections
      result.unshift({
        id: 0,
        groupName,
        parentCode: "",
        parentGroup: "",
        code: "",
        text: "   ",
        description: "",
        sortOrder: -1,
        Icon: "",
      })
    }

    return result.sort((a: CodeItem, b: CodeItem) => a.sortOrder - b.sortOrder)
  },

  /**
   * Get all code values for a group sorted by code
   */
  getCodeValuesSortByCode(groupName: string, required: boolean): Array<CodeItem> {
    return this.getCodeValues(groupName, required).sort((a: CodeItem, b: CodeItem) => {
      if (a.code < b.code) {
        return -1
      }
      if (a.code > b.code) {
        return 1
      }
      return 0
    })
  },

  /**
   * Get all code values for a group sorted by text
   */
  getCodeValuesSortByText(groupName: string, required: boolean): Array<CodeItem> {
    return this.getCodeValues(groupName, required).sort((a: CodeItem, b: CodeItem) => {
      if (a.text < b.text) {
        return -1
      }
      if (a.text > b.text) {
        return 1
      }
      return 0
    })
  },

  /**
   * Get the code value group from the global window object
   */
  getCodeValueGroup(groupName: string): Array<CodeItem> {
    if (typeof window === "undefined") {
      return []
    }

    return (window as any).PhoenixCodeValues?.[groupName] ?? []
  },
}
